/**
 * Direction-aware navigation for Options API components.
 *
 * Distinct moves, deliberately kept apart:
 *
 * - `goBack(fallback)` answers "take me back". It pops the real view stack, so
 *   the header button lands wherever the swipe-back gesture and the Android
 *   hardware key would. Use it for every back affordance.
 * - `goBackTo(destination)` answers "return to X". The destination is fixed
 *   regardless of route history. Use it only for flows that *end* somewhere
 *   specific — finishing a lesson returns to the program landing whether the
 *   reader was opened from the landing or from the Notes Hub.
 * - `goToOrReturn(destination)` answers "open X" for an X that may already BE
 *   the screen behind us. Forward when it isn't, back when it is. Use it for
 *   two screens that each open the other, so a round trip cannot stack
 *   duplicates.
 *
 * Both animate in the 'back' direction. `$router.replace(path)` resolves to
 * `navigate(location, 'root', 'replace')` in Ionic Vue: no push or pop
 * animation, and the router outlet's view stack is flattened. Using it for a
 * move the user reads as "back" makes the button disagree with the swipe-back
 * gesture on the same screen.
 *
 * Implemented as a mixin using the Options API `inject` option rather than
 * `useIonRouter()` in a `setup()` block — AGENTS.md requires Options API
 * throughout, and `setup()` is Composition API. `inject` reads the very same
 * "navManager" that `useIonRouter()` injects, so the behaviour is identical
 * without introducing a second component style.
 *
 * `navManager` is only provided by an <ion-router-outlet> ancestor, so it is
 * declared with a `null` default: components mounted standalone (unit tests,
 * teleported previews) degrade to plain vue-router instead of throwing.
 */
export default {
  inject: {
    navManager: { default: null },
  },
  methods: {
    /**
     * The path the router would return to if popped. vue-router records this on
     * its history state; it is the only reliable way to know what is actually
     * behind us, since `canGoBack()` reports that *something* is there but not
     * what.
     */
    previousRoutePath() {
      const back = this.$router?.options?.history?.state?.back;
      return typeof back === 'string' ? back.split('?')[0] : null;
    },

    /**
     * The same entry as `previousRoutePath`, with its query intact.
     *
     * Dropping the query is right for asking "which screen is behind us", and
     * wrong for the screens whose query IS the screen: the Notes Hub is a
     * different hub per `?lesson=`, so deciding whether to return to the stacked
     * one has to compare the scope too. See `isPreviousRoute`.
     */
    previousRouteFullPath() {
      const back = this.$router?.options?.history?.state?.back;
      return typeof back === 'string' ? back : null;
    },

    /**
     * Is `destination` the entry directly behind us?
     *
     * The path has to match, and so does every query param the destination
     * NAMES. Params it does not name are ignored, because they belong to the
     * stacked screen's own state rather than to the ask: the reader carries a
     * `?page=`, and "the reader for this lesson" is still what is behind us.
     *
     * @param {string|object} destination
     * @returns {boolean}
     */
    isPreviousRoute(destination) {
      const target = this.resolveRoutePath(destination);
      const behind = this.previousRouteFullPath();
      if (!target || !behind) return false;
      const [targetPath, targetSearch = ''] = target.split('?');
      const [behindPath, behindSearch = ''] = behind.split('?');
      if (targetPath !== behindPath) return false;
      const stacked = new URLSearchParams(behindSearch);
      for (const [key, value] of new URLSearchParams(targetSearch)) {
        if (stacked.get(key) !== value) return false;
      }
      return true;
    },

    /**
     * Normalise a route destination to a path string.
     *
     * Returns null for anything that isn't one — notably a DOM Event, which is
     * what arrives when a method is wired straight to BackButton's `custom-back`
     * prop (it calls `customBack(event)`). Falling through to
     * `$router.resolve(event)` there would throw.
     */
    resolveRoutePath(destination) {
      if (typeof destination === 'string') return destination;
      if (!destination || typeof destination !== 'object') return null;
      if (typeof Event !== 'undefined' && destination instanceof Event) return null;
      return this.$router.resolve(destination).fullPath;
    },

    /**
     * Go back to the screen the user actually came from, with the iOS pop.
     *
     * Pops the real stack whenever there is one, so the header button, the
     * swipe-back gesture and the Android hardware key all agree. `fallback` is
     * only reached on a cold open — deep link, push notification, refresh —
     * where nothing was ever stacked behind this view.
     *
     * @param {string|object} [fallback] - Route to use when nothing is stacked
     */
    goBack(fallback) {
      if (this.navManager?.canGoBack?.()) {
        this.navManager.goBack();
        return;
      }
      const target = this.resolveRoutePath(fallback);
      // Nothing behind us and nowhere named to go: better to stay put than to
      // bounce the user somewhere arbitrary.
      if (!target) return;
      if (this.navManager?.handleNavigate) {
        // handleNavigate(location, routerAction, routerDirection)
        this.navManager.handleNavigate(target, 'replace', 'back');
        return;
      }
      this.$router.replace(target);
    },

    /**
     * Navigate back to a KNOWN destination, always with the iOS pop animation.
     *
     * For flows that end at a specific screen rather than at whatever the user
     * came from — completing a lesson returns to the program landing even when
     * the reader was opened from the Notes Hub. For a plain back button you
     * almost certainly want `goBack()` instead.
     *
     * Pops the real stack only when the view behind us IS the destination;
     * otherwise navigates to it directly but still animates in the 'back'
     * direction, so the exit reads the same either way.
     *
     * @param {string|object} destination - Route to return to
     */
    goBackTo(destination) {
      const target = this.resolveRoutePath(destination);
      if (!target) return;
      const targetPath = target.split('?')[0];

      if (this.navManager?.canGoBack?.() && this.previousRoutePath() === targetPath) {
        this.navManager.goBack();
        return;
      }
      if (this.navManager?.handleNavigate) {
        this.navManager.handleNavigate(target, 'replace', 'back');
        return;
      }
      this.$router.replace(target);
    },

    /**
     * Open a destination that may ALREADY be the screen behind us — forward when
     * it isn't, back when it is.
     *
     * For two screens that each open the other: the reader's notes icon opens the
     * Notes Hub, and a card in the hub opens its lesson. Pushing both ways stacks
     * a duplicate on every round trip, and Back then unwinds the ladder one hop
     * at a time — lesson, hub, lesson, hub — which is what members read as the
     * two screens looping rather than as a stack being emptied. Returning keeps
     * the stack the shape the member's mental model already has.
     *
     * The return leg goes through `goBackTo`, so it pops the live view (keeping
     * that screen's own state, e.g. the reader's page) and only falls back to a
     * directed replace when Ionic disagrees about what is stacked.
     *
     * @param {string|object} destination - Route to open
     */
    goToOrReturn(destination) {
      if (this.isPreviousRoute(destination)) {
        this.goBackTo(destination);
        return;
      }
      this.$router.push(destination);
    },

    /**
     * Return to a fixed destination WITHOUT popping.
     *
     * `goBackTo` prefers a real pop when vue-router's `history.state.back` matches
     * the destination, which keeps the outgoing screen's scroll and reads best. The
     * two histories can disagree though: a deep link that REPLACES the current route
     * leaves vue-router pointing at a path that is not what <ion-router-outlet>
     * actually has stacked, so the pop lands somewhere else entirely — Home, in
     * practice, because that is what a scanned link foregrounded the app onto.
     *
     * Use this on a screen that is reached BOTH by tap and by deep link, where a
     * wrong destination costs more than a preserved scroll position. The animation
     * is still the back one, so it reads the same either way.
     *
     * @param {string|object} destination - Route to return to
     */
    navigateBackTo(destination) {
      const target = this.resolveRoutePath(destination);
      if (!target) return;
      if (this.navManager?.handleNavigate) {
        this.navManager.handleNavigate(target, 'replace', 'back');
        return;
      }
      this.$router.replace(target);
    },

    /**
     * Move forward WITHOUT leaving a stack entry behind, with the push animation.
     *
     * For one-way forward moves where returning to the current screen makes no
     * sense — an intro screen handing off to its content, or a menu jumping
     * sideways between siblings. `replace()` gets the stack right but loses the
     * animation; this keeps both.
     *
     * @param {string|object} destination - Route to navigate to
     */
    goForwardReplacing(destination) {
      if (this.navManager?.handleNavigate) {
        this.navManager.handleNavigate(destination, 'replace', 'forward');
        return;
      }
      this.$router.replace(destination);
    },
  },
};
